/* eslint-disable no-restricted-imports */
import clsx from 'clsx'
import React from 'react'
import styles from './UIGameCard.module.scss'
import {CardEnum, PlayerColor} from '@colonist/game-states'
import {Tooltip} from '../Tooltip'
import {T} from '../T'
import {UIGameCount} from '../UIGameCount/UIGameCount'
import {Placement} from '@floating-ui/react'
import {CARD_IMAGES, CARD_TIP_BODY, CARD_TIP_TITLE} from '@colonist/ui-game'
import {TranslationObject} from '@colonist/i18n'
import {UIGameFloatingText} from '../UIGameFloatingText/UIGameFloatingText'
import {useGameStore} from '../../store/GameStoreContext'

const CountBadge = ({count}: { count: number }) => (
    <UIGameCount
        className={styles.countBadge}
        color={PlayerColor.Blue}
        count={count}
    />
)

const RatioBanner = ({tradeRatio}: {tradeRatio: number}) => (
    <div className={styles.ratioBanner}>
        <T id="strings:colonistCommon.utils.#buffer" options={{value: `${tradeRatio}:1`}} />
    </div>
)

interface CardContentProps {
    className: string
    cardEnum: CardEnum
    onClick: VoidFunction | undefined
    count: number | undefined
    tradeRatio?: number | undefined
    greyOut?: boolean | undefined
    highlightAnimation?: boolean | undefined
    bounceAnimation?: boolean | undefined
    size: 'xxs' | 'xs' | 'sm' | 'base'
}

const CardContent = ({
    className,
    cardEnum,
    onClick,
    count,
    tradeRatio,
    greyOut,
    highlightAnimation,
    size,
    bounceAnimation,
}: CardContentProps) => (
    <div
        className={clsx(styles.cardContainer, styles[size],
            {
                [styles.clickable]: onClick != undefined,
                [styles.greyOut]: greyOut,
                [styles.highlight]: highlightAnimation,
                [styles.bounce]: bounceAnimation,
            },
            className)}
        onClick={onClick}
        data-card-enum={cardEnum}
    >
        <img
            draggable={false}
            className={styles.cardImage}
            src={CARD_IMAGES[cardEnum]}
        />
        {count != undefined && <CountBadge count={count} />}
        {tradeRatio != undefined && <RatioBanner tradeRatio={tradeRatio} />}
    </div>
)

export interface UIGameCardProps {
    cardEnum: CardEnum
    className?: string
    onClick?: (cardEnum: CardEnum) => void
    showTipTitle?: boolean
    showTipBody?: boolean
    count?: number
    tooltipPosition?: Placement
    tooltipClassName?: string | undefined
    tradeRatio?: number | undefined
    greyOut?: boolean | undefined
    highlightAnimation?: boolean | undefined
    bounceAnimation?: boolean | undefined
    helperText?: TranslationObject | undefined
    size?: 'xxs' | 'xs' | 'sm' | 'base'
}

const UIGameCardComponent = ({
    className = '',
    cardEnum,
    showTipTitle = false,
    showTipBody = false,
    onClick,
    count = undefined,
    tooltipPosition = 'top',
    tradeRatio,
    greyOut,
    highlightAnimation,
    bounceAnimation,
    helperText,
    size = 'base',
}: UIGameCardProps) => {
    const {translationKeyContext} = useGameStore((state) => state.gameMode)
    const handleClick: VoidFunction | undefined = onClick ? () => onClick(cardEnum) : undefined
    const hasHelperText = helperText != null
    const hasTooltip = showTipTitle || showTipBody
    const tipBody: TranslationObject = {
        ...CARD_TIP_BODY[cardEnum],
        options: {context: translationKeyContext},
    }
    const tipTitle: TranslationObject = {
        ...CARD_TIP_TITLE[cardEnum],
        options: {context: translationKeyContext},
    }
    const cardContent = (
        <CardContent
            className={className}
            count={count}
            cardEnum={cardEnum}
            onClick={handleClick}
            tradeRatio={tradeRatio}
            greyOut={greyOut}
            highlightAnimation={highlightAnimation}
            size={size}
            bounceAnimation={bounceAnimation}
        />
    )

    if(hasTooltip && hasHelperText) {
        return (<Tooltip
            centerText={false}
            content={showTipBody ? <T id={tipBody}/> : undefined}
            menuClassName={styles.tooltipMenu}
            position={tooltipPosition}
            title={showTipTitle ? <T id={tipTitle}/> : undefined}>
            <UIGameFloatingText direction="up" text={helperText}>
                {cardContent}
            </UIGameFloatingText>
        </Tooltip>
        )
    }

    if(hasTooltip) {
        return <Tooltip
            centerText={false}
            content={showTipBody ? <T id={tipBody}/> : undefined}
            menuClassName={styles.tooltipMenu}
            position={tooltipPosition}
            title={showTipTitle ? <T id={tipTitle}/> : undefined}>
            {cardContent}
        </Tooltip>
    }

    if(hasHelperText) {
        return <UIGameFloatingText direction="up" text={helperText} >
            {cardContent}
        </UIGameFloatingText>
    }
    return cardContent
}

export const UIGameCard = React.memo(UIGameCardComponent)
